// 🔼 필수 import 추가
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table, Button, Row, Col, Form, Pagination, Spinner, Accordion
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import { Korean } from "flatpickr/dist/l10n/ko.js";

import useUserMe from "../../../hooks/useUserMe";
import useUserLists from "../../../hooks/useUserLists";
import { deleteUser, getUserListsExport } from "../../../api/auth";
import exportToExcel from "../../../utilities/exportToExcel";
import { joinPathMapping } from "../../../mapping/userMapping";
import { formatDate } from "../../../utilities/dateFormatter";
import { dateSearch } from "../../../utilities/dateUtils";

import { MdAssignment, MdDelete } from "react-icons/md";
import { BiDownload } from "react-icons/bi"; 

import { toast } from "react-toastify";

const UserList = () => {
  const navigate = useNavigate();
  const { data: userMeData } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;

  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState([]);
  const [searchStartDate, setSearchStartDate] = useState(null);
  const [searchEndDate, setSearchEndDate] = useState(null);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [size, setSize] = useState(parseInt(searchParams.get("size") || "10"));
  const [isExporting, setIsExporting] = useState(false);
  const flatpickrRef = useRef(null);

  const [searchField, setSearchField] = useState("name");
  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState(""); // 입력창 상태 추가
  const [programId, setProgramId] = useState("");

  // useUserLists: 검색항목이 프로그램일 때는 inputValue를 programId로 전달
  const effectiveProgramId = searchField === "program" ? (searchValue || programId) : programId;
  const { userLists, totalCount, loading, refresh } = useUserLists(
    storeId, searchStartDate, searchEndDate, page, size, searchField, searchValue, effectiveProgramId || undefined
  );

  // 쿼리스트링에서 dateRange, programId 등 읽어오기
  useEffect(() => {
    const pageParam = searchParams.get("page");
    const sizeParam = searchParams.get("size");
    const startParam = searchParams.get("startDate");
    const endParam = searchParams.get("endDate");
    const programIdParam = searchParams.get("programId");
    if (pageParam) setPage(parseInt(pageParam));
    if (sizeParam) setSize(parseInt(sizeParam));
    if (startParam && endParam) {
      setDateRange([startParam, endParam]);
      setSearchStartDate(startParam);
      setSearchEndDate(endParam);
    }
    if (programIdParam !== null && programIdParam !== undefined) {
      setProgramId(programIdParam);
      if (programIdParam) {
        setSearchField("program");
        setInputValue(programIdParam);
        setSearchValue(programIdParam);
      }
    }
  }, []);

  // --- 쿼리스트링 → 상태 (항상 동기화) ---
  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "1");
    const sizeParam = parseInt(searchParams.get("size") || "10");
    const startParam = searchParams.get("startDate");
    const endParam = searchParams.get("endDate");
    const programIdParam = searchParams.get("programId") || "";

    if (page !== pageParam) setPage(pageParam);
    if (size !== sizeParam) setSize(sizeParam);
    if (startParam && endParam) {
      if (dateRange[0] !== startParam || dateRange[1] !== endParam) {
        setDateRange([startParam, endParam]);
        setSearchStartDate(startParam);
        setSearchEndDate(endParam);
      }
    }
    if (programId !== programIdParam) setProgramId(programIdParam);
    if (programIdParam && searchField !== "program") setSearchField("program");
    // eslint-disable-next-line
  }, [searchParams]);

  // --- 상태 → 쿼리스트링은 직접 액션에서만! ---
  const buildParams = (pageValue, sizeValue, value = searchValue, field = searchField, progId = programId) => {
    const params = { page: String(pageValue), size: String(sizeValue) };
    if (dateRange[0]) params.startDate = dateRange[0];
    if (dateRange[1]) params.endDate = dateRange[1];
    if (value && field) params[field] = value;
    if (field === "program" && value) params.programId = value;
    else if (progId) params.programId = progId;
    return params;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, size));
  };
  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(1);
    setSearchParams(buildParams(1, newSize));
  };
  const handleSearch = () => {
    setSearchStartDate(dateRange[0] || null);
    setSearchEndDate(dateRange[1] || null);
    setPage(1);
    setSearchValue(inputValue);
    if (searchField === "program") setProgramId(inputValue);
    setSearchParams(buildParams(1, size, inputValue, searchField, searchField === "program" ? inputValue : programId));
  };

  const handleDelete = useCallback(async (id) => {
    if (window.confirm("정말 삭제하시겠습니까?\n삭제된 회원은 복구가 불가능합니다.")) {
      try {
        await deleteUser(id);
        alert("삭제되었습니다.");
        refresh();
      } catch (error) {
        alert(error.message);
      }
    }
  }, [refresh]);

  const handleExportToExcel = async () => {
    if (isExporting) {
      toast.warn("엑셀 파일이 준비 중입니다. 잠시만 기다려주세요.");
      return;
    }
    setIsExporting(true);
    let loadingToastId = null;
    try {
      // react-toastify로 토스트 표시
      loadingToastId = toast.info("엑셀 파일을 준비 중입니다...", { autoClose: false });
      // 날짜를 YYYY-MM-DD로 변환
      const formatDateParam = (d) => {
        if (!d) return null;
        if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        const dateObj = new Date(d);
        if (isNaN(dateObj)) return null;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const startDateParam = formatDateParam(searchStartDate);
      const endDateParam = formatDateParam(searchEndDate);
      // 커스텀 API 함수 사용
      const exportProgramId = searchField === "program" ? (searchValue || programId) : programId;
      const data = await getUserListsExport(storeId, startDateParam, endDateParam, exportProgramId || undefined);
      // 엑셀 헤더 정의
      const headers = [
        "번호", "이름", "프로그램", "담당강사", "가입경로",
        "개인레슨 이용기간", "그룹레슨 이용기간", "가입일", "연락처", "생년월일"
      ];
      const formattedData = data.map((row, index) => ({
        번호: index + 1,
        이름: row.name || "-",
        프로그램: row.userProgram?.programName ?? "-",
        담당강사: row.instructorUser?.name ?? "-",
        가입경로: joinPathMapping[row.joinPath] || row.joinPath,
        개인레슨이용기간: row.userProgram?.individualStartDate && row.userProgram?.individualEndDate
          ? `${row.userProgram.individualStartDate} ~ ${row.userProgram.individualEndDate}` : "-",
        그룹레슨이용기간: row.userProgram?.groupStartDate && row.userProgram?.groupEndDate
          ? `${row.userProgram.groupStartDate} ~ ${row.userProgram.groupEndDate}` : "-",
        가입일: formatDate(row.createdAt),
        연락처: row.phone || "-",
        생년월일: row.birth || "-"
      }));
      exportToExcel(formattedData, "user_list", headers);
      toast.dismiss(loadingToastId);
      toast.success("엑셀 파일이 다운로드되었습니다.");
    } catch (err) {
      if (loadingToastId) toast.dismiss(loadingToastId);
      toast.error("엑셀 다운로드에 실패했습니다.");
    } finally {
      setIsExporting(false);
    }
  };

  // 초기화 버튼
  const handleReset = () => {
    setDateRange([]);
    setSearchStartDate(null);
    setSearchEndDate(null);
    flatpickrRef.current?.flatpickr?.clear();
    setSearchField("name");
    setSearchValue("");
    setInputValue(""); // 입력창도 초기화
    setProgramId("");
    setPage(1);
    setSize(10);
    setSearchParams({ page: "1", size: "10" });
  };

  const totalPages = Math.ceil(totalCount / size);

  const getPageNumbers = () => {
    const maxVisible = 10;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div>
      {/* ✅ 검색 옵션 */}
      <Accordion defaultActiveKey="0" className="mb-3">
        <Accordion.Item
          eventKey="0"
          style={{ maxWidth: "600px", fontSize: "0.875rem" }}
        >
          <Accordion.Header>검색 옵션</Accordion.Header>
          <Accordion.Body>
            <Row className="align-items-center mb-1 mt-1 gx-1 gy-1">
              <Col md={2}><Form.Label>가입일</Form.Label></Col>
              <Col md={10}>
                <Flatpickr
                  ref={flatpickrRef}
                  className="form-control"
                  options={{ locale: Korean, mode: "range", dateFormat: "Y-m-d" }}
                  value={dateRange}
                  onChange={(dates) => {
                    dateSearch(dates, (start, end) => {
                      setDateRange([start, end]);
                    });
                  }}
                  placeholder="날짜를 선택하세요"
                />
              </Col>
            </Row>
            <Row className="align-items-center mb-1 gx-1 gy-1">
              <Col md={2}><Form.Label>검색항목</Form.Label></Col>
              <Col md={3}>
                <Form.Select
                  value={searchField}
                  onChange={e => {
                    const v = e.target.value;
                    setSearchField(v);
                    setSearchValue("");
                    setInputValue("");
                    if (v !== "program") setProgramId("");
                  }}
                >
                  <option value="name">이름</option>
                  <option value="phone">연락처</option>
                  <option value="birth">생년월일</option>
                  <option value="program">프로그램</option>
                </Form.Select>
              </Col>
              <Col md={7}>
                <Form.Control
                  type="text"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder={
                    searchField === "name"
                      ? "이름 입력"
                      : searchField === "phone"
                      ? "연락처 입력"
                      : searchField === "birth"
                      ? "YYYYMMDD"
                      : "프로그램명 또는 ID 입력"
                  }
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col className="text-end">
                <Button onClick={handleSearch}>검색</Button>
                <Button variant="secondary" className="ms-2" onClick={handleReset}>초기화</Button>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>


      {/* ✅ 검색 외부에 위치한 상단 우측 정보 */}
      <Row className="mb-2 justify-content-end">
        {/* 엑셀 다운로드 버튼 (권한: 관리자 전용) */}
        {userMeData && userMeData.instructor.position === 'DIRECTOR' && (
        <Col md="auto" className="text-start">
          <Button
            variant="success"
            className="me-2"
            onClick={handleExportToExcel}
            disabled={isExporting}
          >
            <BiDownload size={18} className="me-1" />
            엑셀
          </Button>
        </Col>
        )}
        <Col md="auto" className="text-end">
          <span className="me-3">
            총 {totalCount}건 / {totalPages}페이지
          </span>
          <Form.Select
            value={size}
            onChange={(e) => handleSizeChange(Number(e.target.value))}
            style={{ width: "100px", display: "inline-block" }}
          >
            {[10, 20, 50, 100].map((s) => (
              <option key={s} value={s}>{s}개씩</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* ✅ 사용자 목록 테이블 */}
      {loading ? (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>이름</th>
                <th>프로그램</th>
                <th>담당강사</th>
                <th>가입경로</th>
                <th>개인레슨</th>
                <th>그룹레슨</th>
                <th>가입일</th>
                <th>연락처</th>
                <th>생년월일</th>
                <th>문진표</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {userLists.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center">검색결과가 없습니다.</td>
                </tr>
              ) : (
                userLists.map((user, idx) => (
                  <tr key={user.id}>
                    <td>{(page - 1) * size + idx + 1}</td>
                    <td>
                      <a href={`/user/user-edit/${user.id}`} style={{ textDecoration: "underline" }}>
                        {user.name}
                      </a>
                    </td>
                    <td>{user.userProgram?.programName ?? "-"}</td>
                    <td>{user.instructorUser?.name ?? "-"}</td>
                    <td>{joinPathMapping[user.joinPath] || user.joinPath}</td>
                    <td>{user.userProgram?.individualStartDate ? `${user.userProgram.individualStartDate} ~ ${user.userProgram.individualEndDate}` : "-"}</td>
                    <td>{user.userProgram?.groupStartDate ? `${user.userProgram.groupStartDate} ~ ${user.userProgram.groupEndDate}` : "-"}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{user.phone}</td>
                    <td>{user.birth}</td>
                    <td>
                      {/* 문진표 */}
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => navigate(`/user/user-survey/${user.id}`)}
                      >
                        <MdAssignment size={18} />
                      </Button>
                    </td>
                    <td>
                      {/* 삭제 */}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(user.id)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* ✅ 페이지네이션 */}
          <Pagination className="justify-content-center mt-2">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1} />
            {getPageNumbers().map((p) => (
              <Pagination.Item
                key={p}
                active={p === page}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
          </Pagination>
        </>
      )}
    </div>
  );
};

export default UserList;
